import { useMemo, useState, useCallback } from 'react';
import { StylishNewSearchBar } from '../../../DesignSystems/New/StylishNewSearchBar';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { ActiveFilterChips } from '../LayersTab/ActiveFilterChips';
import {
  AORHierarchyNode,
  AREA,
  DISTRICT,
  SECTOR,
  SUBUNIT,
} from './utils/schema';
import { useGetAorsByHierarchy } from '../../../../hooks/useGetAorsByHierarchy';
import { useAors } from 'hooks/useAors';
import { HierarchyItemCard } from './HierarchyItemCard';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { SharedIcon } from '../../../SharedIcon/SharedIcon';
import { useResourceSets } from '../../../IAP/hooks/resourceSetHooks';
import ResourceTabCard from '@/components/MapPage/CorporateViewTabs/ResourceTab/ResourceTabCard';
import { useGetRostersForGroup } from '@/hooks/incident/useRoster';
import { useGroupGuid } from '@/components/IAP/hooks/commonHooks';
import { mapResourceSetToResourceRow } from '@/components/MapPage/CorporateViewTabs/ResourceTab/ResourceView';

const AORs = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [focusedDistrictId, setFocusedDistrictId] = useState<string | null>(
    null
  );
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
  const { aorFilterIds, setAorFilterIds, isAorVisible, toggleAorVisibility } =
    useUscgMapContext();

  const { data: aorsByHierarchy = [], isLoading } = useGetAorsByHierarchy();
  const { data: allAors = [], isLoading: isAllAorsLoading } = useAors();
  const { data: resourceSets = [], isLoading: isResourceSetsLoading } =
    useResourceSets();
  const groupId = useGroupGuid();
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId);

  // AORs created in Organization Settings have no hierarchy level, so the
  // hierarchy endpoint never returns them. They are listed flat below the tree.
  const ungroupedAors = useMemo<AORHierarchyNode[]>(
    () =>
      allAors
        .filter((aor) => !aor.level_name)
        .map((aor) => ({
          id: aor.id,
          name: aor.name,
          description: aor.description,
          parentItemId: null,
          location: typeof aor.location === 'string' ? aor.location : null,
          currentLocationGeom: aor.current_location_geom ?? null,
        })),
    [allAors]
  );

  const allAorIds = useMemo(
    () => [
      ...aorsByHierarchy.flatMap((level) => level.aors).map((aor) => aor.id),
      ...ungroupedAors.map((aor) => aor.id),
    ],
    [aorsByHierarchy, ungroupedAors]
  );

  const isAllVisible = useMemo(
    () => isAorVisible && aorFilterIds === null,
    [aorFilterIds, isAorVisible]
  );

  const handleToggleAll = useCallback(() => {
    if (isAllVisible) {
      setAorFilterIds([]);
      toggleAorVisibility();
      return;
    }

    if (!isAorVisible) {
      toggleAorVisibility();
    }

    setAorFilterIds(null);
  }, [isAllVisible, isAorVisible, setAorFilterIds, toggleAorVisibility]);

  const areas = useMemo(
    () => aorsByHierarchy.find((level) => level.levelName === AREA)?.aors ?? [],
    [aorsByHierarchy]
  );

  const districts = useMemo(
    () =>
      aorsByHierarchy.find((level) => level.levelName === DISTRICT)?.aors ?? [],
    [aorsByHierarchy]
  );

  const districtsByAreaId = useMemo(() => {
    return districts.reduce<Record<string, AORHierarchyNode[]>>(
      (acc, district) => {
        if (!district.parentItemId) {
          return acc;
        }

        if (!acc[district.parentItemId]) {
          acc[district.parentItemId] = [];
        }

        acc[district.parentItemId].push(district);
        return acc;
      },
      {}
    );
  }, [districts]);

  const visibleAreasWithDistricts = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return areas
      .map((area) => {
        let areaDistricts = districtsByAreaId[area.id] ?? [];

        if (focusedDistrictId) {
          areaDistricts = areaDistricts.filter(
            (district) => district.id === focusedDistrictId
          );
        } else if (normalizedSearch.length) {
          areaDistricts = areaDistricts.filter((district) =>
            district.name.toLowerCase().includes(normalizedSearch)
          );
        }

        return {
          area,
          districts: areaDistricts,
        };
      })
      .filter(({ districts: areaDistricts }) => areaDistricts.length > 0);
  }, [areas, districtsByAreaId, focusedDistrictId, searchText]);

  const visibleUngroupedAors = useMemo(() => {
    // Drilling into a district or sector narrows the list to that branch
    if (focusedDistrictId || selectedSectorId) return [];

    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch.length) return ungroupedAors;

    return ungroupedAors.filter((aor) =>
      aor.name.toLowerCase().includes(normalizedSearch)
    );
  }, [focusedDistrictId, searchText, selectedSectorId, ungroupedAors]);

  const sectorsByDistrictId = useMemo(() => {
    const sectors =
      aorsByHierarchy.find((level) => level.levelName === SECTOR)?.aors ?? [];

    return sectors.reduce<Record<string, AORHierarchyNode[]>>((acc, sector) => {
      if (!sector.parentItemId) {
        return acc;
      }

      if (!acc[sector.parentItemId]) {
        acc[sector.parentItemId] = [];
      }

      acc[sector.parentItemId].push(sector);
      return acc;
    }, {});
  }, [aorsByHierarchy]);

  const subUnitsBySectorId = useMemo(() => {
    const subUnits =
      aorsByHierarchy.find((level) => level.levelName === SUBUNIT)?.aors ?? [];

    return subUnits.reduce<Record<string, AORHierarchyNode[]>>(
      (acc, subUnit) => {
        if (!subUnit.parentItemId) {
          return acc;
        }

        if (!acc[subUnit.parentItemId]) {
          acc[subUnit.parentItemId] = [];
        }

        acc[subUnit.parentItemId].push(subUnit);
        return acc;
      },
      {}
    );
  }, [aorsByHierarchy]);

  const sectorsById = useMemo(() => {
    return Object.values(sectorsByDistrictId)
      .flat()
      .reduce<Record<string, AORHierarchyNode>>((acc, sector) => {
        acc[sector.id] = sector;
        return acc;
      }, {});
  }, [sectorsByDistrictId]);

  const districtsById = useMemo(() => {
    return districts.reduce<Record<string, AORHierarchyNode>>(
      (acc, district) => {
        acc[district.id] = district;
        return acc;
      },
      {}
    );
  }, [districts]);

  const areasById = useMemo(() => {
    return areas.reduce<Record<string, AORHierarchyNode>>((acc, area) => {
      acc[area.id] = area;
      return acc;
    }, {});
  }, [areas]);

  const resourceSetsByAorId = useMemo(() => {
    return resourceSets.reduce<Record<string, typeof resourceSets>>(
      (acc, resourceSet) => {
        if (!resourceSet.opcon && !resourceSet.opfac_details?.[0].aor_id) {
          return acc;
        }

        if (resourceSet.type === 'Personnel') {
          return acc;
        }

        const aorId =
          resourceSet.opcon || resourceSet.opfac_details?.[0].aor_id;

        if (!acc[aorId]) {
          acc[aorId] = [];
        }

        acc[aorId].push(resourceSet);
        return acc;
      },
      {}
    );
  }, [resourceSets]);

  const activeArea = selectedAreaId
    ? (areasById[selectedAreaId] ?? null)
    : null;
  const activeDistrict = focusedDistrictId
    ? (districtsById[focusedDistrictId] ?? null)
    : null;
  const activeSector = selectedSectorId
    ? (sectorsById[selectedSectorId] ?? null)
    : null;

  const activeChips = useMemo(() => {
    if (!activeDistrict || !activeArea) {
      return [];
    }

    const chips = [
      {
        filterName: 'Area',
        filterValue: activeArea.name,
        onRemove: () => {
          setSelectedAreaId(null);
          setFocusedDistrictId(null);
          setSelectedSectorId(null);
        },
      },
      {
        filterName: 'District',
        filterValue: activeDistrict.name,
        onRemove: () => {
          setSelectedSectorId(null);
          setFocusedDistrictId(null);
        },
      },
    ];

    if (selectedSectorId && activeSector) {
      chips.push({
        filterName: 'Sector',
        filterValue: activeSector.name,
        onRemove: () => {
          setSelectedSectorId(null);
        },
      });
    }

    return chips;
  }, [activeArea, activeDistrict, activeSector, selectedSectorId]);

  return (
    <div className="d-flex flex-column gap-2 p-2" style={{ height: '100%' }}>
      <div
        className="position-sticky top-0 pb-2"
        style={{
          borderBottom: '1px solid #2b2f38',
        }}
      >
        <div className="d-flex mb-2 gap-2">
          <StylishNewSearchBar
            value={searchText}
            onChangeSearchTerm={setSearchText}
          />
          <StylishNewButton
            className="button--icon"
            title={
              isAllVisible ? 'Hide all AORs from map' : 'Show all AORs on map'
            }
            aria-label={isAllVisible ? 'Hide all AORs' : 'Show all AORs'}
            onClick={handleToggleAll}
          >
            <SharedIcon
              iconName={isAllVisible ? 'visibility' : 'visibility_off'}
              size="sm"
            />
          </StylishNewButton>
        </div>
        {focusedDistrictId && activeDistrict && (
          <div className="mt-2">
            <ActiveFilterChips chips={activeChips} />
          </div>
        )}
      </div>
      <div className="flex-grow-1 overflow-auto" style={{ maxHeight: '60vh' }}>
        {isLoading || isAllAorsLoading || isResourceSetsLoading ? (
          <div className="text-muted py-4 text-center">Loading AORs...</div>
        ) : selectedSectorId ? (
          <>
            {(subUnitsBySectorId[selectedSectorId] ?? []).map((subUnit) => (
              <HierarchyItemCard
                key={subUnit.id}
                allAorIds={allAorIds}
                item={subUnit}
                sectionKey={`subunit-${subUnit.id}`}
                marginClass="mb-2"
              >
                <div>
                  {(resourceSetsByAorId[subUnit.id] ?? []).map(
                    (resourceSet) => {
                      const resourceRow = mapResourceSetToResourceRow(
                        resourceSet,
                        groupMembers
                      );
                      return (
                        <ResourceTabCard
                          row={resourceRow}
                          key={resourceSet.id}
                          groupMembers={groupMembers}
                          compact={true}
                          shouldShowId={false}
                          setShowCheckInDrawer={() => {}}
                          setShowCheckOutDrawer={() => {}}
                          viewOnly={true}
                        />
                      );
                    }
                  )}
                </div>
              </HierarchyItemCard>
            ))}
          </>
        ) : visibleAreasWithDistricts.length === 0 &&
          visibleUngroupedAors.length === 0 ? (
          <div className="text-muted py-4 text-center">
            {searchText.trim().length
              ? 'No AORs match your search.'
              : 'No AORs to display.'}
          </div>
        ) : (
          <>
            {visibleAreasWithDistricts.map(
              ({ area, districts: areaDistricts }) => (
                <HierarchyItemCard
                  key={area.id}
                  allAorIds={allAorIds}
                  descendantIds={[
                    ...areaDistricts.map((district) => district.id),
                    ...areaDistricts.flatMap((district) =>
                      (sectorsByDistrictId[district.id] ?? []).map(
                        (sector) => sector.id
                      )
                    ),
                    ...areaDistricts.flatMap((district) =>
                      (sectorsByDistrictId[district.id] ?? []).flatMap(
                        (sector) =>
                          (subUnitsBySectorId[sector.id] ?? []).map(
                            (subUnit) => subUnit.id
                          )
                      )
                    ),
                  ]}
                  item={area}
                  sectionKey={`area-${area.id}`}
                  marginClass="mb-2"
                >
                  <div>
                    {areaDistricts.map((district) => (
                      <HierarchyItemCard
                        key={district.id}
                        allAorIds={allAorIds}
                        descendantIds={[
                          ...(sectorsByDistrictId[district.id] ?? []).map(
                            (s) => s.id
                          ),
                          ...(sectorsByDistrictId[district.id] ?? []).flatMap(
                            (s) =>
                              (subUnitsBySectorId[s.id] ?? []).map(
                                (su) => su.id
                              )
                          ),
                        ]}
                        item={district}
                        sectionKey={`district-${district.id}`}
                        marginClass="mb-2"
                      >
                        <div>
                          {(sectorsByDistrictId[district.id] ?? []).map(
                            (sector) => {
                              const hasSubUnits =
                                (subUnitsBySectorId[sector.id]?.length ?? 0) >
                                0;
                              return (
                                <HierarchyItemCard
                                  key={sector.id}
                                  allAorIds={allAorIds}
                                  level={SECTOR}
                                  descendantIds={(
                                    subUnitsBySectorId[sector.id] ?? []
                                  ).map((su) => su.id)}
                                  item={sector}
                                  sectionKey={`sector-${sector.id}`}
                                  onAction={
                                    hasSubUnits
                                      ? () => {
                                          setSelectedAreaId(area.id);
                                          setFocusedDistrictId(district.id);
                                          setSelectedSectorId(sector.id);
                                        }
                                      : undefined
                                  }
                                  actionLabel={
                                    hasSubUnits
                                      ? 'Show Sector SubUnits'
                                      : undefined
                                  }
                                  noActionMessage={
                                    !hasSubUnits ? 'No SubUnits' : undefined
                                  }
                                  marginClass="mb-2"
                                >
                                  <div className="mt-2">
                                    <span className="mt-2">Resources:</span>
                                    {resourceSetsByAorId[sector.id]?.map(
                                      (resourceSet) => {
                                        const resourceRow =
                                          mapResourceSetToResourceRow(
                                            resourceSet,
                                            groupMembers
                                          );
                                        return (
                                          <ResourceTabCard
                                            row={resourceRow}
                                            key={resourceSet.id}
                                            groupMembers={groupMembers}
                                            compact={true}
                                            shouldShowId={false}
                                            setShowCheckInDrawer={() => {}}
                                            setShowCheckOutDrawer={() => {}}
                                            viewOnly={true}
                                          />
                                        );
                                      }
                                    )}
                                  </div>
                                </HierarchyItemCard>
                              );
                            }
                          )}
                        </div>
                        {resourceSetsByAorId[district.id] && (
                          <div>
                            <span className="mt-2">Resources:</span>
                            {resourceSetsByAorId[district.id].map(
                              (resourceSet) => {
                                const resourceRow = mapResourceSetToResourceRow(
                                  resourceSet,
                                  groupMembers
                                );
                                return (
                                  <ResourceTabCard
                                    row={resourceRow}
                                    key={resourceSet.id}
                                    groupMembers={groupMembers}
                                    compact={true}
                                    shouldShowId={false}
                                    setShowCheckInDrawer={() => {}}
                                    setShowCheckOutDrawer={() => {}}
                                    viewOnly={true}
                                  />
                                );
                              }
                            )}
                          </div>
                        )}
                      </HierarchyItemCard>
                    ))}
                  </div>
                </HierarchyItemCard>
              )
            )}
            {visibleUngroupedAors.map((aor) => (
              <HierarchyItemCard
                key={aor.id}
                allAorIds={allAorIds}
                item={aor}
                sectionKey={`aor-${aor.id}`}
                marginClass="mb-2"
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default AORs;
